import React, { useRef, useState } from "react";
import { CloudUpload, ShieldCheck } from "lucide-react";

const carriesFiles = (e) =>
  Array.from(e.dataTransfer?.types || []).includes("Files");

/** Drop zone (or click to browse) for one or more Sensi PDF reports. */
const SensiDropzone = ({ onFiles, maxFileMb }) => {
  const inputRef = useRef(null);
  // dragenter/dragleave fire for every child the pointer crosses, so count
  // them rather than toggling on each one.
  const depthRef = useRef(0);
  const [dragging, setDragging] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleDragEnter = (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    depthRef.current += 1;
    setDragging(true);
  };

  const handleDragOver = (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    if (!carriesFiles(e)) return;
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    depthRef.current = 0;
    setDragging(false);
    // The drop's file list is unreadable once this handler returns.
    onFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handlePicked = (e) => {
    const files = Array.from(e.target.files || []);
    // Cleared so picking the same file again still fires a change.
    e.target.value = "";
    onFiles(files);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload Sensi PDF reports: drag and drop, or press to browse"
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-14 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
          dragging
            ? "border-blue-500 bg-blue-50"
            : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/40"
        }`}
      >
        <span
          className={`mb-4 grid h-14 w-14 place-items-center rounded-full transition-colors ${
            dragging ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600"
          }`}
        >
          <CloudUpload className="h-7 w-7" aria-hidden="true" />
        </span>
        <p className="text-base font-semibold text-slate-800">
          {dragging ? "Drop to upload" : "Drag & drop Sensi PDF reports here"}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          or <span className="font-medium text-blue-600">browse your files</span>
        </p>
        <p className="mt-3 text-xs text-slate-400">
          PDF only · up to {maxFileMb} MB each · add as many files as you need
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={handlePicked}
      />

      <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
        <ShieldCheck
          className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
          aria-hidden="true"
        />
        Albert reads each report and keeps only its data. The PDF itself is
        deleted once it has been read.
      </p>
    </div>
  );
};

export default SensiDropzone;
